import Spacer from "@components/ui/Spacer";
import AutoLayout from "@src/deprecatedDesignSystem/components/AutoLayout";
import Text from "@ui/text";
import { useModal } from "@hooks/useModal";
import LinkHrisEmployeeModal from "@src/components/modals/LinkHrisEmployeeModal";
import ContextMenuItem from "@src/deprecatedDesignSystem/components/ContextMenu/ContextMenuItem";
import DeprecatedIcon from "@src/deprecatedDesignSystem/components/DeprecatedIcon";
import { useHris } from "@src/hooks/useHris";
import { StyleSheet } from "aphrodite";
import { format, parseISO } from "date-fns";
import { gql } from "@apollo/client";
import { FC, useMemo } from "react";
import PaginatedTable from "@src/deprecatedDesignSystem/components/table/PaginatedTable";
import { useHrisSearchFilters } from "./hooks/useHrisSearchFilters";
import { HrisEmployeesInput } from "@src/types.generated";
import HrisFilters from "./HrisFilters";
import {
  HrisEmployeeFragment,
  useHrisEmployeesLazyQuery,
  useHrisEmployeesQuery,
} from "./HrisEmployeeListContainer.generated";
import MergeLinkButton from "./MergeLinkButton";
import EmptyState from "@src/components/ui/EmptyState";
import { listToReadableString } from "@src/utils/strings";

const columns = [
  "name",
  "hrisId",
  "status",
  "location",
  "role",
  "syncedAt",
] as const;
type Column = (typeof columns)[number];

const exportColumns = [
  "firstName",
  "lastName",
  "hrisId",
  "status",
  "location",
  "role",
  "syncedAt",
] as const;
type ExportColumn = (typeof exportColumns)[number];

type Props = {
  excludeColumns?: Column[];
  showOverflowMenu?: boolean;
  showInviteFilter?: boolean;
  inHrisCsvSetup?: boolean;
  hideTitle?: boolean;
};
const HrisEmployeeListContainer: FC<Props> = ({
  excludeColumns,
  showOverflowMenu = true,
  showInviteFilter,
  inHrisCsvSetup = false,
  hideTitle = false,
}) => {
  const { hrisSearchFilters } = useHrisSearchFilters();
  const { showModal } = useModal();

  const hrisIntegrationStatus = useHris();

  const inputFilters: HrisEmployeesInput = useMemo(() => {
    return {
      filters: {
        locationIds:
          hrisSearchFilters.locationIds.length > 0
            ? { value: hrisSearchFilters.locationIds }
            : undefined,
        roleIds:
          hrisSearchFilters.roleIds.length > 0
            ? { value: hrisSearchFilters.roleIds }
            : undefined,
        inviteStatus:
          hrisSearchFilters.inviteStatus !== undefined
            ? { value: hrisSearchFilters.inviteStatus }
            : undefined,
      },
    };
  }, [hrisSearchFilters]);

  if (
    hrisIntegrationStatus.isIntegrated === false &&
    !hrisIntegrationStatus.hasAppliedHrisCsvUpload &&
    !inHrisCsvSetup
  ) {
    return (
      <div style={{ height: "100%", paddingBottom: 200 }}>
        <EmptyState
          textBold="Your HRIS is not connected yet"
          description="Integrate with your HRIS to see who hasn't signed up for Opus"
          imgPath="/images/integrations.png"
          className={styles.emptyState}
          imgClassName={styles.emptyImg}
          buttonComponent={<MergeLinkButton />}
        />
      </div>
    );
  }

  return (
    <AutoLayout
      direction={"vertical"}
      alignSelf={"stretch"}
      paddingTop={4}
      flex={1}
    >
      <PaginatedTable<
        HrisEmployeeFragment,
        Column,
        typeof useHrisEmployeesQuery,
        typeof useHrisEmployeesLazyQuery,
        ExportColumn
      >
        title={hideTitle ? undefined : "HRIS Employees"}
        defaultSort={{ column: "syncedAt", descending: true }}
        queryHook={useHrisEmployeesQuery}
        lazyQueryHook={useHrisEmployeesLazyQuery}
        getItems={(x) => x?.HrisEmployees?.objects ?? []}
        getTotalItemCount={(x) => x?.HrisEmployees?.totalCount ?? 0}
        itemId={(x) => x.id}
        input={inputFilters}
        rightAction={<HrisFilters showInviteFilter={showInviteFilter} />}
        visibleColumns={
          excludeColumns
            ? () => columns.filter((x) => !excludeColumns.includes(x))
            : undefined
        }
        exportConfig={{
          title: "hris_employees.csv",
          exportColumns: excludeColumns
            ? () => {
                const filteredExportColumns = columns.filter(
                  (x) => !excludeColumns.includes(x) && x !== "name",
                ) as ExportColumn[];
                filteredExportColumns.push("firstName", "lastName");
                return filteredExportColumns;
              }
            : () => exportColumns,
          exportFns: {
            firstName: (x) => x.firstName,
            lastName: (x) => x.lastName,
            location: (x) =>
              listToReadableString(
                x.locationMemberships.map((memb) => memb.location.name),
              ),
            role: (x) =>
              listToReadableString(
                x.teamMemberships.map((memb) => memb.team.name),
              ),
            status: (x) => (x.user ? "invited" : "not invited"),
            syncedAt: (x) => format(parseISO(x.createdAt), "M/d/yyyy"),
            hrisId: (x) => x.remoteHrisId,
          },
        }}
        allColumns={[
          "name",
          "hrisId",
          "status",
          "location",
          "role",
          "syncedAt",
        ]}
        columnTitles={{
          name: "Name",
          hrisId: `${
            hrisIntegrationStatus.provider ? hrisIntegrationStatus.provider : ""
          } ID`,
          location: "Location",
          role: "Role",
          status: "Status",
          syncedAt: "Synced from HRIS",
        }}
        renderFns={{
          name: (x) => (
            <Text
              type={"P2"}
              fontWeight={"Medium"}
              ellipsis
              ellipsisParentFlexDirection={"row"}
            >
              {`${x.firstName} ${x.lastName}`}
            </Text>
          ),
          location: (x) =>
            listToReadableString(
              x.locationMemberships.map((memb) => memb.location.name),
            ),
          role: (x) =>
            listToReadableString(
              x.teamMemberships.map((memb) => memb.team.name),
            ),
          status: (x) =>
            x.user ? (
              <AutoLayout alignmentVertical="center">
                <DeprecatedIcon type="checkmark" />
                <Text type="P2">Invited</Text>
              </AutoLayout>
            ) : (
              <AutoLayout alignmentVertical="center">
                <DeprecatedIcon type="warning-triangle" />
                <Text type="P2">Not invited</Text>
              </AutoLayout>
            ),
          syncedAt: (x) => format(parseISO(x.createdAt), "M/d/yyyy"),
          hrisId: (x) => x.remoteHrisId,
        }}
        overflowMenu={(x) => {
          if (!showOverflowMenu) {
            return null;
          }
          return (
            <>
              <ContextMenuItem
                label={x.user ? "Edit" : "Link"}
                icon={"linked"}
                onSelect={() => {
                  showModal(<LinkHrisEmployeeModal hrisEmployee={x} />);
                }}
              />
            </>
          );
        }}
      />
      <Spacer size={64} />
    </AutoLayout>
  );
};

gql`
  fragment EmployeeListHrisLocation on HrisLocation {
    id
    name
  }
  fragment EmployeeListHrisTeam on HrisTeam {
    id
    name
  }
  fragment HrisEmployee on HrisEmployee {
    id
    createdAt
    firstName
    lastName
    phoneNumber
    remoteHrisId
    locationMemberships {
      id
      location {
        ...EmployeeListHrisLocation
      }
    }
    teamMemberships {
      id
      team {
        ...EmployeeListHrisTeam
      }
    }
    employmentStatus
    user {
      id
      name
    }
  }
  query HrisEmployees(
    $input: HrisEmployeesInput!
    $pagination: PaginationInput
  ) {
    HrisEmployees(input: $input, pagination: $pagination) {
      totalCount
      objects {
        ...HrisEmployee
      }
    }
  }
`;

const styles = StyleSheet.create({
  emptyState: {
    height: 500,
  },
  emptyImg: {
    maxWidth: 240,
  },
  importingMessage: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
});

export default HrisEmployeeListContainer;
